<template lang="pug">
.selection-container(@click="uiStore.hideOverlay()")
  .selection-area.main-container-wide(@click.stop)
    .sticky-header
      .header
        h5.location-container
          Icon.location-icon(name="location")
          span.location-text {{__('Select Delivery Location & Language')}}
          button.a11y.close(@click="uiStore.hideOverlay()" aria-label="Close")
            Icon(name="x" fill="rgb(162, 162, 162)" )
        .mobile-bottom-text {{__('International orders to countries not listed below should select United States as their region.')}}
        .grow
      Tabs.region-select-tabs(:tabs="regionNames" v-model="currentRegion")
    .regions
      .desktop-top-text {{__('International orders to countries not listed below should select United States as their region.')}}
      .country-grid(v-for="region in regions" :class="{'selected': (currentRegion === region.name)}")
        h2 {{__(region.name)}}
        .countries
          a.a11y.country(
            v-for="country in region.countries"
            :key="country.code+country.lang"
            @click.prevent="selectCountry(country, $event, currentRegion === region.name)"
            :href="country.href"
          )
            Icon.flag(:name="country.code === 'APO' ? 'flag-' + country.code.toLowerCase() : 'flags/' + country.code.toUpperCase()")
            .name {{ __(country.name) }}
              .language {{ __(langs[country.lang || 'en']) }}
              .description {{ country.description }}
</template>
<script setup lang="ts">
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useMQ } from 'sisu/js/mq'
import { useRoute } from 'vue-router'
import { regions as countryRegions, type Region, type Country } from '../../../helpers/countries'
import { setCookie } from '../../../services/Utils'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'

const uiStore = useUIStore()
const rogue = useRogue()
const mq = useMQ()
const route = useRoute()

const regions = ref<Region[]>(countryRegions)
const currentRegion = ref('Americas')
const langs = {
  en : 'English',
  es : 'Spanish',
  nl : 'Dutch',
  fr : 'French',
  de : 'German',
}
const envs: { [key: string]: string } = {
  sprint  : 'm2sprint',
  staging : 'm2staging',
  test    : 'm2test',
}
const locales = {
  en : 'en-GB',
  es : 'es-ES',
  nl : 'nl-NL',
  fr : 'fr-FR',
  de : 'de-DE',
}

const regionNames = computed<string[]>(() => regions.value.map(r => r.name))

if (rogue.site.code === 'EUR' || rogue.site.code === 'GBR') {
  currentRegion.value = 'Europe'
}

if (rogue.site.code === 'AUS') {
  currentRegion.value = 'Asia Pacific'
}

regions.value.forEach(r => {
  r.countries.forEach(c => {
    const prefix = c.prefix || r.prefix
    // @todo alltocom Eventually we will move AU and CA to .com as well...
    if (c.code !== 'AS' && c.code !== 'CA') {
      // In non-DEV environments use an absolute URL so navigation from roguecanada.ca
      // or rogueaustralia.com.au lands on the correct roguefitness.com hostname.
      const base = import.meta.env.DEV ? '' : `https://${envs[rogue.env] || 'www'}.roguefitness.com`
      let path = prefix ? `/${prefix}` : ''
      if (prefix === 'eu' && c.lang && c.lang !== 'en') {
        path += `/${c.lang}`
      }
      path += '/'
      if (prefix === 'eu') {
        c.href = `${base}${path}?_sdst=${c.code}`
      } else {
        c.href = `${base}${path}`
      }
    } else {
      if (import.meta.env.DEV) {
        // In DEV use path-based routing to stay on localhost
        const prefix = c.prefix || r.prefix
        c.href = prefix ? `/${prefix}/` : '/'
      } else {
        c.href = `https://${envs[rogue.env] || 'www'}.${c.host || r.host}/`
      }
      if (r.prefix === 'eu') {
        c.href += `?_sdst=${c.code}&locale=${locales[c.lang || 'en']}`
      }
    }
  })
})

function selectCountry(country: Country, event: Event, sameRegion: boolean) {
  event.stopPropagation()
  event.preventDefault()

  rogue.track.track({ type: 'custom', event: 'Change Region', data: { oldRegion: rogue.l10n.locale, newRegion: country.code } })

  setCookie('preferredCountry', country.code)

  const newUrl = new URL(country.href || '', window.location.origin)

  if (sameRegion) {
    if (country.code === 'US' || country.code === 'APO') {
      // To ensure APO and US paths work correctly
      country.href = newUrl.pathname
    } else {
      // If its in the same region outside of the US, we can go to the same route just with the current (non-localized) path
      const localePrefix = newUrl.pathname.replace(/\/$/, '')
      const currentPath = route.path.replace(/\/$/, '')

      // A deep page (currentPath truthy) never ends in a slash; landing on the
      // locale's own root (currentPath empty) always does.
      newUrl.pathname = currentPath ? `${localePrefix}${currentPath}` : `${localePrefix}/`
    }
  }

  const href = import.meta.env.DEV ? newUrl.pathname + newUrl.search : newUrl.toString()

  setTimeout(() => window.location.href = href, 50)                                  
}

onMounted(() => {
  if (mq.value !== 'mobile' && mq.value !== 'tablet') {
    window.scrollTo(0, 0)
  }
  preventScroll()
})

onBeforeUnmount(() => {
  allowScroll()
})
</script>
<style lang="scss" scoped>

$header-height-desktop: 38px;

.selection-container {
  position: fixed;
  z-index: 99999;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgba(0, 0, 0, 0.75);

  @include all-desktop(){
    height: calc(100vh - $header-height-desktop);
    top: $header-height-desktop;
  }
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: $color-white;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 $space-xs;
  border-bottom: 1px solid #E9E9E9;
  margin-bottom: $size-xxl;

  .location-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding-top: $size-xs;
  }

  .location-icon {
    margin-right: $space-xxxs;
  }

  .location-text {
    margin-right: auto;
    font-size: $size-xs;
  }

  @include mobile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0;

  .mobile-bottom-text {
    font-size: $size-xs;
    margin-bottom: $space-xs;
  }
  }

  .mobile-bottom-text {
    @include not-mobile {
    display: none;
  }

  }

  h5 {
    font-size: $size-xs;
    font-family: Roboto-Bold, $font-base;
  }
}

.selection-area {
  padding: 0 $space-xxxs;
  background: $color-white;
  overflow-y: auto;

  @include not-desktop(){
    height: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  @include not-mobile(){
    border-radius: 0 0 3px 3px;
    box-shadow: 0 0 4px -2px $color-gray-dark;
  }
}

.desktop-top-text {
  padding-left: $space-s;
  margin-bottom: $size-xxl;
  font-family: Roboto-Bold, $font-base;

  @include mobile {
    display: none;
  }
}

.country-grid {
  padding-left: $space-s;
  order: 0;

  &:not(.selected) {
    order: 1;

    @include mobile(){
      display: none;
    }
  }
}

.countries {
  overflow-x: hidden;
  @include not-mobile() {
    font-size: $size-m;
    padding-bottom: $space-s;
    display: grid;
    grid-template-columns: repeat(6, 16.6%);
  }
  @include tablet() {
    display: grid;
    grid-template-columns: repeat(3, 33.33%);
  }
}

.name {
  display: flex;
  flex-direction: column;
  color: black;
  text-decoration: none;
  font-size: $size-s;
  max-width: 200px;
}

.language {
  color: gray;
  padding-top: $space-xxs;
  font-size: $size-xxs;
}

.description {
  padding-top: $space-xxs;
  font-size: $size-xs;
  font-style: italic;
}

.regions {
  display: flex;
  flex-direction: column;
}

.country.a11y {
  display: flex;
  color: black;
  cursor: pointer;
  text-decoration: none;
  padding: $space-xs;
}

.flag {
  margin-right: $space-xxs;
}

@include not-mobile(){
  .region-select-tabs {
    display: none;
  }
}

</style>
